import { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./useAuth";
import { NotificationContext } from "../context/NotificationContext";
import { ZDARZENIA_SYNC } from "./useSynchronizacjaPowiadomien";

/**
 * Maps notification type codes → sidebar section paths (partial match).
 * When a notification with one of these codes arrives, the matching
 * sidebar item gets a green "new content" dot.
 */
/* [O-6 audytu, 2026-09-08] Z tej mapy zdjęto SZEŚĆ martwych adresów, które wyglądały na działające:
   `/e-learning` (realne trasy to `/szkolenia` i `/elearning`) przy czterech kodach oraz `/wnioski-dzialu`
   przy `hr_pending_approval` (dziś `/moj-dzial/wnioski`). Żaden z nich niczego nie gasił — przy każdym
   kodzie stał obok adres poprawny — ale wpis wskazujący nieistniejącą trasę jest CICHY i utrwala złudzenie
   pokrycia (lekcja F12). Pilnuje tego teraz `__tests__/kropkiPowiadomienPakietu.o6`. */
/* [domknięcie luk 2026-10-01] Eksport tylko do odczytu — zapadka kropek Mój Dział → Procedury czyta mapę wprost. */
export const NOTIFICATION_SECTION_MAP = {
    // Komunikaty
    announcement_new: ["/centrum-powiadomien", "/notification-center", "/moje-komunikaty", "/my-announcements"],
    // Aktualności
    news_published: ["/aktualnosci", "/news"],
    // Galeria
    gallery_published: ["/galeria", "/gallery"],
    // Konkursy
    contest_started: ["/konkursy", "/contests"],
    contest_results: ["/konkursy", "/contests"],
    // E-Learning — przypisane szkolenie / rozszerzony kurs / zbliżający się termin (kropka „masz coś do zrobienia")
    elearning_published: ["/szkolenia", "/elearning"],
    elearning_updated: ["/szkolenia", "/elearning"],
    elearning_deadline: ["/szkolenia", "/elearning"],
    // Dokumenty
    document_published: ["/dokumenty", "/documents"],
    // [POLITYKI 2026-09-28, E7 D31] Obowiązek zapoznania → kropka przy „Do potwierdzenia” (własna trasa, nie /dokumenty —
    // link powiadomienia stoi poza sitem NotificationLinkScope); decyzja i przegląd → kropka przy „Polityki i procedury”.
    polityka_do_potwierdzenia: ["/do-potwierdzenia", "/to-acknowledge"],
    polityka_przypomnienie: ["/do-potwierdzenia", "/to-acknowledge"],
    polityka_zalegla: ["/do-potwierdzenia", "/to-acknowledge"],
    // [POTWIERDZENIA 2026-09-29] eskalacja zaległości do PRZEŁOŻONEGO — własny typ; [poprawka 3] dzwonek prowadzi do widoku
    // „Zaległości zgłoszone do Ciebie” na /do-potwierdzenia (pulpit Mojego Działu nic o zaległości nie mówił) — tam też kropka.
    polityka_eskalacja: ["/do-potwierdzenia", "/to-acknowledge"],
    polityka_do_zatwierdzenia: ["/polityki", "/policies"],
    // [P4 2026-10-01, „Cykl życia na pasku”] Redakcja pracuje pod JEDNYM adresem modułu (/admin/procedury-bezpieczenstwo — pozycja
    // „Procedury i bezpieczeństwo” w grupie menu ZGODNOŚĆ), a /polityki jest już tylko przekierowaniem: kropki redakcji świecą przy
    // nowym adresie (podstrona /admin, nie cała sekcja), stare ścieżki zostają dla starych linków.
    /* [domknięcie luk 2026-10-01] + Mój Dział → „Procedury”: kierownik i właściciel bez klucza pracują tam (grupy ZGODNOŚĆ nie mają),
       więc kropka powiadomień o ich procedurach świeci przy tej podpozycji, a nie tylko przy adresie redakcji. */
    polityka_przeglad: ["/polityki", "/policies", "/admin/procedury-bezpieczenstwo", "/moj-dzial/procedury", "/my-dept/procedures"],
    // [ZATWIERDZANIE 2026-09-29, W-2] Wynik obiegu (przegląd zakończony, runda odrzucona z komentarzem, zatwierdzona) → kropka
    // przy „Procedury i bezpieczeństwo” u właściciela i wysyłającego (redakcja).
    polityka_obieg: ["/polityki", "/policies", "/admin/procedury-bezpieczenstwo", "/moj-dzial/procedury", "/my-dept/procedures"],
    // [2026-09-29] Biblioteka procedur: ogłoszenie publikacji (link do „Do potwierdzenia” albo Dokumentów firmowych), przypisanie właściciela
    // i termin kontroli okresowej (karta procedury na /polityki).
    polityka_opublikowana: ["/do-potwierdzenia", "/to-acknowledge", "/dokumenty", "/documents"],
    polityka_wlasciciel: ["/polityki", "/policies", "/admin/procedury-bezpieczenstwo", "/moj-dzial/procedury", "/my-dept/procedures"],
    polityka_kontrola: ["/polityki", "/policies", "/admin/procedury-bezpieczenstwo", "/moj-dzial/procedury", "/my-dept/procedures"],
    // [E8 2026-09-30, D44] Wykaz KSC — link powiadomień to WŁASNA trasa /wykaz-ksc (poza sitem NotificationLinkScope, lekcja D31);
    // zmiana danych, terminy, eskalacja, awaria źródła, przegląd i zestawienie → kropka przy „Wykaz KSC”; zmiana prawa → /wykaz-ksc/prawo.
    wykaz_zmiana_danych: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_termin: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_po_terminie: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_zrodlo_awaria: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_przeglad: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_zestawienie: ["/wykaz-ksc", "/ksc-registry"],
    wykaz_prawo_zmiana: ["/wykaz-ksc/prawo", "/ksc-registry/law"],
    // [O365-Z 2026-10-04, W-2] Zarządzanie Office 365 — nowy incydent, alert W/K, przegląd dzienny i alert zdrowia zbierania zapalają
    // kropkę przy podstronie panelu /admin/office365 (tam prowadzi link powiadomienia: ?spolka=…&zakladka=…). Powiadomienia idą
    // wyłącznie do posiadaczy klucza Office365View w zasięgu spółki (serwer: IO365Odbiorcy), więc kropka nie trafia do nikogo innego.
    o365bezp_incydent: ["/admin/office365"],
    o365bezp_alert: ["/admin/office365"],
    o365bezp_digest: ["/admin/office365"],
    o365bezp_zdrowie: ["/admin/office365"],
    // Baza wiedzy (artykuły + FAQ)
    kb_article_published: ["/baza-wiedzy", "/knowledge-base"],
    faq_published: ["/baza-wiedzy", "/knowledge-base"],
    // [2026-09-30, polecenie właściciela G] Obowiązek zapoznania się z artykułem bazy wiedzy (i przypomnienie o nim) —
    // kropka tam, gdzie się to załatwia: przy „Bazie wiedzy”.
    kb_zapoznanie_wymagane: ["/baza-wiedzy", "/knowledge-base"],
    kb_zapoznanie_przypomnienie: ["/baza-wiedzy", "/knowledge-base"],
    // Kanban
    kanban_task_assigned: ["/kanban"],
    kanban_task_due: ["/kanban"],
    kanban_task_updated: ["/kanban"],
    kanban_board_shared: ["/kanban"],
    kanban_task_comment: ["/kanban"],
    kanban_member_removed: ["/kanban"],
    // Zadania (TODO) — przypisanie / udostępnienie / ukończenie / edycja
    task_assigned: ["/zadania", "/tasks"],
    task_shared: ["/zadania", "/tasks"],
    task_completed: ["/zadania", "/tasks"],
    task_updated: ["/zadania", "/tasks"],
    // Ankiety
    survey_available: ["/ankiety", "/surveys"],
    // Listy projektowe — dodanie do projektu / przypisanie pozycji / zbliżający się termin
    pl_project_added: ["/projekty", "/projects"],
    pl_item_assigned: ["/projekty", "/projects"],
    pl_item_due: ["/projekty", "/projects"],
    // [P9] Grupy projektów — dodanie do grupy (link powiadomienia: /projekty/grupa/{id})
    pl_group_added: ["/projekty", "/projects"],
    // Wnioski HR — wnioskodawca (status własnego wniosku) → /wnioski-hr
    hr_request_status: ["/wnioski-hr", "/hr-requests"],
    /* [2026-09-08] SKIEROWANIE WNIOSKU PRZEZ KADRY — kodu `hr_request_assigned`
       (HrDelegationService.cs:32) NIE BYŁO W TEJ MAPIE, więc „Dział HR skierował do Ciebie wniosek"
       nie zapalało ANI JEDNEJ kropki: człowiek dowiadywał się o zleceniu wyłącznie z dzwonka albo
       z maila. Znalezione przy dokładaniu kropek do paseków zakładek.
       Dwa adresy, bo skierowanie widac w DWÓCH miejscach: na własnej liście wniosków (dokąd prowadzi
       odnośnik powiadomienia — `AssignmentsPagePath`) i w zakładce „Moje do wypełnienia" Mojego Działu. */
    hr_request_assigned: [
        "/wnioski-hr", "/hr-requests",
        "/moj-dzial/wnioski-przelozonego", "/my-dept/manager-requests",
    ],
    /* Wnioski HR — akceptant: wniosek czeka na decyzję.
       [zgłoszenie właściciela 2026-09-03, poz. 1c] BYŁO ŹLE: jeden kod zapalał kropkę PRZY OBU
       wejściach naraz. Odkąd kolejka „Mój Dział" pokazuje wyłącznie sprawy WŁASNYCH ludzi, kropka
       przy „Mój Dział" prowadziła kadrową do listy, na której tego wniosku nie ma — i nie gasła,
       bo nie było czego odwiedzić. Mapa jest kluczowana wyłącznie kodem typu, więc rozdzielenie
       miejsc wymagało rozdzielenia kodów (backend: HrService.HrTypePendingKadry). */
    hr_pending_approval: ["/moj-dzial/wnioski", "/my-dept/requests"],
    hr_pending_approval_kadry: ["/obsluga-hr", "/hr-management"],
    /* [2026-09-16] SYGNALISTA — moduł nie zapalał DOTĄD ŻADNEJ kropki: żadnego z jego kodów nie było
       w tej mapie, więc o nowym zgłoszeniu i o odpowiedzi zgłaszającego człowiek dowiadywał się wyłącznie
       z dzwonka albo z maila. Sprawa ma termin ustawowy, więc cisza w menu jest tu najdroższym błędem.
       Kropka zapala się WYŁĄCZNIE u tego, kto dostał powiadomienie (a te idą tylko do posiadaczy klucza
       `SygnalistaManage`), więc zakres jest ten sam co wysyłki — nic dodatkowego nie wycieka.
       Oba warianty trasy, bo adresy są dwujęzyczne (routes.js: `hrSygnalista`). */
    sygnalista_new_report: ["/obsluga-hr/sygnalista", "/hr-management/whistleblower"],
    sygnalista_new_message: ["/obsluga-hr/sygnalista", "/hr-management/whistleblower"],
    sygnalista_assigned: ["/obsluga-hr/sygnalista", "/hr-management/whistleblower"],
    // Mój Dział — alert dla przełożonego: niski stan urlopu w zespole
    hr_low_leave_balance: ["/moj-dzial/urlopy", "/my-dept/leave"],
    // Mój Dział — wniosek urlopowy pracownika czeka na decyzję przełożonego (żywa kolejka „do zatwierdzenia")
    vacation_pending: ["/moj-dzial/urlopy", "/my-dept/leave"],

    /* ── [AKCEPTACJE 2026-08-29] Rodzina „coś czeka na Twoją decyzję" ────────────────────────────
       Kropka ma prowadzić tam, GDZIE SIĘ TO ZAŁATWIA, a nie tam, skąd przyszło zgłoszenie.
       Plany urlopowe i praca zdalna są rozpatrywane w „Mój Dział"; decyzja zwrotna wraca
       pracownikowi do „Mój czas pracy". Rodziny „Odpowiedzi" świadomie tu NIE MA: ten sam kod
       (comment_reply) dotyczy raz aktualności, raz projektu, a mapa jest kluczowana wyłącznie
       kodem typu — kropka trafiałaby więc co drugi raz w niewłaściwą zakładkę. Odpowiedzi mają
       licznik przy dzwonku i osobną zakładkę w centrum powiadomień. */
    vacation_plan_pending: ["/moj-dzial/urlopy", "/my-dept/leave"],
    vacation_plan_decided: ["/moj-czas-pracy", "/my-work-time"],
    remote_work_pending: ["/moj-dzial/praca-zdalna", "/my-dept/remote"],
    remote_work_decided: ["/moj-czas-pracy", "/my-work-time"],
    // Regulamin: akceptacja odbywa się w blokującym oknie na dowolnej stronie, ale ślad sprawy
    // (i historia zaakceptowanych wersji) jest w centrum powiadomień.
    terms_acceptance_required: ["/centrum-powiadomien", "/notification-center"],
    /* ══ [PAKIET 2026-09-02] 36 nowych typów pięciu modułów ═════════════════════════════════════
       ZASADA (ta sama, co przy rodzinie „Do akceptacji"): kropka prowadzi tam, GDZIE SIĘ TO ZAŁATWIA,
       a nie tam, skąd przyszło. Dlatego typy dla właściciela celu wskazują „Moje cele", a typy dla
       przełożonego — „Mój Dział"; ten sam kod nigdy nie stoi w obu rolach naraz.
       Ścieżki muszą być PREFIKSAMI adresów realnych pozycji menu — mapowanie na adres, którego nie ma
       w sidebarze, jest ciche i nic nie robi (lekcja F12: martwe wpisy ticket_* wyglądały na działające
       przez wiele miesięcy). Wpisy `/admin/…` i `/obsluga-hr/onboarding` wskazują wejścia z landingu HR
       i panelu — świadomie, bo tam prowadzi treść powiadomienia. */

    // ── Ścieżka kariery (G) ─────────────────────────────────────────────────────────────────────
    career_achievement_decided: ["/moja-sciezka-kariery", "/my-career-path"],
    career_competency_assessed: ["/moja-sciezka-kariery", "/my-career-path"],
    career_event_added: ["/moja-sciezka-kariery", "/my-career-path"],
    // Szkolenie przypisane i wygasający certyfikat załatwia się w E-Learningu, nie na osi kariery.
    career_training_assigned: ["/szkolenia", "/elearning"],
    career_certificate_expiring: ["/szkolenia", "/elearning"],
    /* [O-6 audytu, domknięte 2026-09-08] PLAN ROZWOJU — oba typy istniały w seederze od 2026-09-04,
       ale NIE BYŁO ich w tej mapie, więc żadne z nich nie zapalało zielonej kropki: pracownik dowiadywał
       się o zwrocie planu do poprawy dopiero wtedy, gdy sam wszedł na stronę. Adresy wzięte WPROST
       z odnośników w powiadomieniach (`CareerPathService.Plan.cs`) — przełożony rozpatruje plan w ocenie
       pracownika, decyzja wraca na oś kariery pracownika. */
    career_plan_submitted: ["/moj-dzial/ocena-pracownika", "/my-dept/evaluation"],
    career_plan_reviewed: ["/moja-sciezka-kariery", "/my-career-path"],

    // ── Cele (H) ────────────────────────────────────────────────────────────────────────────────
    goal_assigned: ["/moje-cele", "/my-goals"],
    goal_accepted: ["/moje-cele", "/my-goals"],
    goal_rejected: ["/moje-cele", "/my-goals"],
    goal_due_soon: ["/moje-cele", "/my-goals"],
    goal_overdue: ["/moje-cele", "/my-goals"],
    goal_evaluated: ["/moje-cele", "/my-goals"],
    // Trzy typy dla drugiej strony: propozycja do akceptacji, cel zgłoszony jako zrobiony, termin oceny.
    goal_proposed: ["/moj-dzial/cele", "/my-dept/goals", "/obsluga-hr"],
    goal_completed: ["/moj-dzial/cele", "/my-dept/goals", "/obsluga-hr"],
    goal_review_due: ["/moj-dzial/cele", "/my-dept/goals", "/obsluga-hr"],

    // ── Oferty pracy (J) ────────────────────────────────────────────────────────────────────────
    job_offer_published: ["/oferty-pracy", "/job-offers"],
    // Aplikację i polecenie rozpatrują kadry w panelu (/admin/oferty-pracy) — tam prowadzi też link
    // powiadomienia. Kandydat i polecający wracają na ogłoszenie.
    job_application_received: ["/admin/oferty-pracy"],
    job_referral_received: ["/admin/oferty-pracy"],
    job_referral_bonus_due: ["/admin/oferty-pracy"],
    job_application_status: ["/oferty-pracy", "/job-offers"],
    job_referral_status: ["/oferty-pracy", "/job-offers"],

    // ── Dokumenty działu (K) i dokumenty zatrudnienia (I-a) ─────────────────────────────────────
    // Kody `deptdoc_*` zamiast pierwotnych `document_*` — te ostatnie byłyby podciągiem kodów I-a
    // (`hr_document_issued`), a mapa kropek i pula odbiorców HR filtrują po LIKE '%kod%'.
    deptdoc_issued: ["/obsluga-hr", "/hr-management"],
    deptdoc_cancelled: ["/obsluga-hr", "/hr-management"],
    deptdoc_template_assigned: ["/moj-dzial/dokumenty", "/my-dept/documents"],
    hrdoc_issued: ["/moje-dokumenty-hr", "/my-hr-documents", "/admin/issued-documents"],
    hrdoc_signed: ["/moje-dokumenty-hr", "/my-hr-documents", "/admin/issued-documents"],

    // ── Onboarding (I-b) ────────────────────────────────────────────────────────────────────────
    onboarding_case_created: ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    onboarding_item_done: ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    onboarding_account_linked: ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    onboarding_start_soon: ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    onboarding_completed: ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    // Pozycja przypisana imiennie (przełożony, BHP, IT) — odhacza się ją w „Zadaniach".
    onboarding_item_assigned: ["/zadania", "/tasks"],

    // ── Offboarding (I-c) ───────────────────────────────────────────────────────────────────────
    offboarding_started: ["/admin/offboarding", "/e-teczka"],
    offboarding_it_completed: ["/admin/offboarding", "/e-teczka"],
    offboarding_due_reminder: ["/admin/offboarding", "/e-teczka"],
    offboarding_closed: ["/admin/offboarding", "/e-teczka"],
    // Kod z rozmysłem NIE brzmi `offboarding_task_assigned`: zawierałby istniejący `task_assigned`.
    offboarding_position_assigned: ["/zadania", "/tasks"],

    // [F12] USUNIĘTE martwe mapowania ticket_* → /zgloszenia|/obsluga-it: żaden element sidebara nie ma
    // tych ścieżek (wejście do obsługi jest w Header, który nie konsumuje hasNew), więc kropka nigdy się
    // nie renderowała — była to no-op konfiguracja. Kropka na przycisku „Obsługa …" = świadomy backlog
    // (wymaga współdzielonego stanu badge, nie drugiej instancji tego hooka). Powiadomienia app/mail
    // dla ticket_* działają niezależnie (centrum powiadomień).
};

/**
 * Maps real-time SignalR window events → sidebar section paths.
 * Kropka pojawia się na NOWĄ TREŚĆ (nowy wpis / aktualizacja), NIE na komentarze
 * (komentarze nie emitują tych zdarzeń). Zdarzenia są filtrowane po widoczności na
 * backendzie, więc kropkę dostają tylko uprawnieni.
 *
 * DODAWANIE KOLEJNYCH ZAKŁADEK: backend emituje "<modul>-upserted"/"<modul>-removed"
 * (jak news/galeria), a tutaj dopisujesz wpis event→sekcje. To cały koszt nowej kropki.
 */
const REALTIME_EVENT_SECTION_MAP = {
    "news-upserted": ["/aktualnosci", "/news"],
    "gallery-upserted": ["/galeria", "/gallery"],
    "contest-upserted": ["/konkursy", "/contests"],
    "kb-article-upserted": ["/baza-wiedzy", "/knowledge-base"],
    "faq-upserted": ["/baza-wiedzy", "/knowledge-base"],
    "document-upserted": ["/dokumenty", "/documents"],
    // [A09-22] Ankiety — dotąd kropka przy „Ankiety" zapalała się wyłącznie z powiadomienia
    // „nowa ankieta", więc każda PÓŹNIEJSZA zmiana (dołożone pytanie, przesunięty termin, ankieta
    // udostępniona kolejnemu działowi) przechodziła bez śladu w menu.
    "survey-upserted": ["/ankiety", "/surveys"],
    // [polecenie właściciela 2026-09-14] Pakiet szkoleń przypisany / zmieniony → kropka przy „Szkolenia” (oba języki trasy).
    "pakiet-szkolen-upserted": ["/szkolenia", "/elearning"],
    "task-upserted": ["/zadania", "/tasks"],
    "pl-project-changed": ["/projekty", "/projects"],
    // Mój Dział — realtime (SignalR): zmiana wniosku HR / struktury działu → kropka „nowa aktywność".
    // Dropdown „Mój Dział" i tak widzi tylko posiadacz ModuleMojDzial, więc kropka trafia do właściwych.
    "hr-request-changed": ["/moj-dzial/wnioski", "/my-dept/requests"],
    "dept-structure-changed": ["/moj-dzial/struktura", "/my-dept/structure"],
    /* [brief właściciela 2026-08-31, pkt 4.2] „trzeba dodać kropkę w /obsluga-hr sygnalizującą,
       że coś się dzieje". Komentarz na wniosku HR trafia do stron i akceptantów (backend wysyła
       zdarzenie imiennie, nie do wszystkich), więc kropka zapala się dokładnie tym, których
       sprawa dotyczy — i gaśnie po wejściu na sekcję, jak każda inna kropka tego hooka.
       Kropka stoi na WSZYSTKICH trzech wejściach do wniosków, bo komentarz może dotyczyć
       zarówno sprawy kadr (/obsluga-hr), jak i własnego wniosku (/wnioski-hr) albo wniosku
       podwładnego (/moj-dzial). Świadomie NIE dopisujemy tu kodu powiadomienia
       `comment_on_content`: tym samym kodem lecą komentarze aktualności i projektów, więc kropka
       trafiałaby co drugi raz w niewłaściwą zakładkę — dokładnie ten błąd, przez który z mapy
       powiadomień wypadło `comment_reply`. */
    "hr-comment-added": ["/obsluga-hr", "/wnioski-hr", "/hr-requests", "/moj-dzial/wnioski", "/my-dept/requests"],

    /* [PAKIET 2026-09-02] Sześć rodzin zdarzeń pięciu nowych modułów (nadawcy: `NotificationContext.js`).
       „document-issue-upserted" jest JEDNO dla dokumentów działu (K) i dokumentów zatrudnienia (I-a),
       bo rejestr wydań jest jeden — dwa zdarzenia o tym samym zapisie zapalałyby dwie kropki. */
    "career-changed": ["/moja-sciezka-kariery", "/my-career-path", "/moj-dzial/ocena-pracownika", "/my-dept/evaluation"],
    "goal-upserted": ["/moje-cele", "/my-goals", "/moj-dzial/cele", "/my-dept/goals"],
    "job-offer-upserted": ["/oferty-pracy", "/job-offers"],
    "document-issue-upserted": ["/moj-dzial/dokumenty", "/my-dept/documents", "/obsluga-hr", "/hr-management"],
    "onboarding-upserted": ["/obsluga-hr/onboarding", "/hr-management/onboarding", "/admin/onboarding"],
    "offboarding-changed": ["/admin/offboarding"],
    /* [POLITYKI REDAKCJA-poprawka 2 2026-09-29, W-3] Przegląd: „założenie, zamrożenie czy publikacja polityki przez innego redaktora
       nie zapala zielonej kropki przy «Polityki i procedury»; kropkę zapalają tylko typy powiadomień”. Zdarzenie huba PolitykaUpserted
       dochodzi wyłącznie do osób z kluczem modułu, właściciela i autora (serwer: RedOdbiorcyAsync), więc kropka trafia do właściwych. */
    "polityka-upserted": ["/polityki", "/policies", "/admin/procedury-bezpieczenstwo"],
};

// v2: reset kropek menu po naprawie powiadomień (2026-07-11) — czyści zaległe kropki do treści
// ukrytych/usuniętych. Świeży, pusty stan; kropka wraca tylko dla realnie nowej treści (bramka odbiorców).
//
// [A15-10] BYŁO ZLE: to były STAŁE, globalne nazwy kluczy w localStorage — jeden zestaw „odwiedzin"
// na całą przeglądarkę, wspólny dla wszystkich kont, i nieczyszczony przy wylogowaniu.
// SKUTEK DLA UŻYTKOWNIKA: na wspólnym stanowisku (hala, recepcja, komputer zmianowy) kolejna
// zalogowana osoba dziedziczyła „odwiedziny" poprzednika — zielone kropki przy Aktualnościach czy
// Bazie wiedzy były już zgaszone, więc NIE zauważała nowej treści, której sama nigdy nie widziała.
// W drugą stronę: widziała kropki przy sekcjach, których zawartość jej nie dotyczy.
// NAPRAWA: klucz dostaje przyrostek z identyfikatorem konta, więc każdy pracownik ma własne
// „odwiedziny". Stan celowo NIE jest kasowany przy wylogowaniu — po ponownym zalogowaniu ta sama
// osoba ma odzyskać swoje wygaszone kropki, a nie dostać ich wszystkich od nowa.
export const STORAGE_KEY_BASE = "sidebar_last_visited_v2";
export const STORAGE_KEY_CONTENT_BASE = "sidebar_latest_content_v2";

// Konto nieznane (hook zamontowany przed odczytaniem sesji) dostaje własny, oddzielny kubełek —
// nigdy nie wracamy do globalnej nazwy, bo to ona była źródłem podglądania cudzych kropek.
export const badgeStorageKeys = (accountId) => {
    const suffix = accountId === null || accountId === undefined || accountId === "" ? "anon" : String(accountId);
    return {
        visited: `${STORAGE_KEY_BASE}:${suffix}`,
        content: `${STORAGE_KEY_CONTENT_BASE}:${suffix}`,
    };
};

/**
 * Returns the section key for a given path.
 * We normalise to the first segment: /news/123 → /news
 */
function pathToSectionKey(pathname) {
    if (!pathname || pathname === "/") return "/";
    const parts = pathname.split("/").filter(Boolean);
    return "/" + parts[0];
}

/* [zgłoszenie właściciela 2026-09-05] KROPKA PRZY PODPOZYCJI, NIE TYLKO PRZY RODZICU.
   BYŁO ŹLE: cały stan hooka był kluczowany PIERWSZYM segmentem ścieżki, więc „coś czeka we wnioskach
   Twojego działu" i „ktoś zmienił strukturę" były tą samą kropką przy „Mój Dział" — a rozwinięte menu
   nie mówiło, KTÓRA z szesnastu podzakładek jest tą sprawą. SKUTEK: kierownik klikał po kolei, aż trafił.
   NAPRAWA: treść mapowana na ścieżkę dwusegmentową („/moj-dzial/wnioski") ląduje pod kluczem podstrony;
   treść mapowana na sekcję („/aktualnosci") zostaje pod kluczem sekcji i — jak dotąd — dotyczy całej sekcji
   (wpis /aktualnosci/123 dzieli stan z /aktualnosci). Wizyta zapisuje OBA poziomy; kropka podstrony gaśnie
   wyłącznie wizytą na tej podstronie, kropka sekcji — wizytą gdziekolwiek w sekcji. */
function pathToPageKey(pathname) {
    if (!pathname || pathname === "/") return "/";
    const parts = pathname.split("/").filter(Boolean);
    return parts.length >= 2 ? "/" + parts[0] + "/" + parts[1] : "/" + parts[0];
}
const jestKluczemPodstrony = (key) => key.split("/").filter(Boolean).length >= 2;

function readStore(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

/**
 * Hook that manages green "new content" dots on sidebar items.
 *
 * Returns:
 *  - hasNew(path): boolean — true if item at `path` should show the dot
 *  - markVisited(path): void — clears the dot for `path`
 *  - onNotification(notification): void — feed from NotificationContext (typeCode → dot)
 */
/** Straz modulowa dla zapytania startowego o agregat komentarzy — patrz komentarz przy efekcie.
 *  Trzyma id konta, dla ktorego juz pobrano agregat w tej sesji przegladarki. */
let agregatKomentarzyPobrany = null;

export function useSidebarBadges() {
    const location = useLocation();

    // [A15-10] Identyfikator zalogowanego konta — decyduje, CZYJE „odwiedziny" czytamy i zapisujemy.
    // useAuth poza providerem zwraca undefined (np. w testach hooka), stąd optional chaining.
    const accountId = useAuth()?.user?.id ?? null;
    const { visited: STORAGE_KEY, content: STORAGE_KEY_CONTENT } = useMemo(
        () => badgeStorageKeys(accountId),
        [accountId]
    );

    // { "/news": "2026-02-16T12:00:00Z", ... } — kiedy ostatnio odwiedzono sekcję
    const [lastVisited, setLastVisited] = useState(() => readStore(STORAGE_KEY));

    // { "/news": "2026-02-16T13:00:00Z", ... } — timestamp najnowszej treści per sekcja
    // (persistowane, by kropka przetrwała odświeżenie aż do wizyty).
    const [latestContent, setLatestContent] = useState(() => readStore(STORAGE_KEY_CONTENT));

    // [A15-10] Zmiana konta BEZ przeładowania strony (wylogowanie i zalogowanie w tej samej karcie)
    // przełącza klucze — stan w pamięci trzeba wczytać na nowo, inaczej nowy użytkownik przez chwilę
    // patrzyłby na kropki poprzednika. Pierwszy przebieg jest pominięty: stan początkowy pochodzi
    // już z właściwego klucza, a ponowny setState powodowałby zbędny render.
    const poprzednieKlucze = useRef(STORAGE_KEY);
    useEffect(() => {
        if (poprzednieKlucze.current === STORAGE_KEY) return;
        poprzednieKlucze.current = STORAGE_KEY;
        setLastVisited(readStore(STORAGE_KEY));
        setLatestContent(readStore(STORAGE_KEY_CONTENT));
    }, [STORAGE_KEY, STORAGE_KEY_CONTENT]);

    const persist = useCallback((key, value) => {
        try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
    }, []);

    /* ⚠️ [WYDAJNOŚĆ 2026-09-12] ADRES BIEŻĄCEJ STRONY CZYTANY Z REFA, NIE Z DOMKNIĘCIA.

       CO BYŁO ŹLE: `markContent` miał `location.pathname` w zależnościach, więc jego TOŻSAMOŚĆ
       zmieniała się przy każdej nawigacji. A wisiały na niej trzy rzeczy:
         • efekt nasłuchu zdarzeń na żywo — przy KAŻDYM skoku w menu odpinał i przypinał od nowa
           26 nasłuchów `window` (tyle liczy `REALTIME_EVENT_SECTION_MAP`);
         • `onNotification`, czyli to, co `Sidebar` rejestruje w `NotificationContext` — rejestracja
           wypisywała się i zapisywała przy każdej nawigacji;
         • efekt zapytania startowego o agregat komentarzy (`/api/hr-requests/comment-activity`),
           który przed strażą modułową z 2026-09-08 szedł do serwera przy każdym przejściu.
       Ta ostatnia pułapka była już raz naprawiona STRAŻĄ; tu usuwam jej PRZYCZYNĘ, żeby nie wróciła
       przy dołożeniu kolejnego konsumenta (lekcja: straż leczy objaw, niestabilna zależność wraca).

       DLACZEGO REF JEST TU POPRAWNY, A NIE „SPRYTNY": `markContent` czyta adres w CHWILI WYWOŁANIA
       (przyszło powiadomienie / zdarzenie SignalR), a nie w chwili tworzenia funkcji. Ref oddaje
       wartość świeższą niż domknięcie — zachowanie jest identyczne albo bardziej poprawne. */
    const sciezkaRef = useRef(location.pathname);
    sciezkaRef.current = location.pathname;

    /* Z TEGO SAMEGO POWODU klucze localStorage czytamy z refa. Zmieniają się RAZ na sesję —
       w chwili, gdy poznajemy identyfikator konta (przed nim hook pracuje na kubełku „anon") —
       ale dopóki stały w zależnościach `markContent`, ta jedna zmiana też odpinała i przypinała
       cały nasłuch i powodowała powtórną rejestrację kropek w `NotificationContext`.
       Zapis idzie zawsze pod klucz AKTUALNEGO konta, bo ref jest odświeżany przy każdym renderze. */
    const kluczeRef = useRef({ visited: STORAGE_KEY, content: STORAGE_KEY_CONTENT });
    kluczeRef.current = { visited: STORAGE_KEY, content: STORAGE_KEY_CONTENT };

    /* [2026-09-30, polecenie właściciela B3] KROPKI GASNĄ WE WSZYSTKICH KARTACH PRZEGLĄDARKI.
       BYŁO: stan kropek żył w localStorage, ale każda karta czytała go RAZ, przy montowaniu. Wejście do sekcji w jednej
       karcie zapisywało „odwiedzone”, a druga karta dalej świeciła kropką do przeładowania. Przeglądarka ogłasza zapis
       localStorage z INNEJ karty zdarzeniem `storage` — słuchamy go dla kluczy TEGO konta i wczytujemy stan na nowo.
       (Własne zapisy tej karty tego zdarzenia nie wywołują, więc nie ma pętli.) */
    useEffect(() => {
        const naZapisZInnejKarty = (e) => {
            const { visited, content } = kluczeRef.current;
            const wszystko = e?.key == null; // localStorage.clear() w innej karcie
            if (wszystko || e.key === visited) setLastVisited(readStore(visited));
            if (wszystko || e.key === content) setLatestContent(readStore(content));
        };
        window.addEventListener("storage", naZapisZInnejKarty);
        return () => window.removeEventListener("storage", naZapisZInnejKarty);
    }, []);

    /* [2026-09-30, polecenie właściciela B3] ODCZYT POWIADOMIENIA GASI KROPKĘ SEKCJI — TAKŻE NA INNYM URZĄDZENIU.
       Serwer rozgłasza odczyt do wszystkich połączeń konta (`NotificationRead` / `NotificationsReadMany`, re-emitowane przez
       NotificationContext jako zdarzenia okna). Gdy dla kodu typu przeczytanej pozycji NIE została już żadna nieprzeczytana
       pozycja, sekcja z NOTIFICATION_SECTION_MAP tego kodu jest „zobaczona”. To przybliżenie: kropka tej sekcji zapalona
       zdarzeniem na żywo (nie powiadomieniem) zgaśnie razem z nią. `ticket_*` dalej nie mają kropek (F12).
       Lista powiadomień z kontekstu czytana z refa: zdarzenie przychodzi, zanim kontekst przeliczy stan. */
    const kontekstPowiadomien = useContext(NotificationContext);
    const powiadomieniaRef = useRef([]);
    powiadomieniaRef.current = Array.isArray(kontekstPowiadomien?.notifications) ? kontekstPowiadomien.notifications : [];
    useEffect(() => {
        const naOdczyt = (e) => {
            const surowe = e?.type === ZDARZENIA_SYNC.readMany ? e?.detail : [e?.detail];
            if (!Array.isArray(surowe) || surowe.length === 0) return;
            const ids = new Set(surowe.filter((x) => x != null).map(String));
            if (ids.size === 0) return;
            const lista = powiadomieniaRef.current;
            const kody = new Set(lista.filter((n) => ids.has(String(n.id)) && n.typeCode).map((n) => n.typeCode));
            /* [2026-09-30, B3 — przegląd adwersarialny] „ZOSTAŁO” LICZYMY PER SEKCJA, NIE PER KOD. Mapa sprowadza wiele kodów
               do jednej sekcji (/kanban 6, /zadania 4, /baza-wiedzy 4, /do-potwierdzenia 4, /projekty 4). BYŁO: odczyt
               kanban_task_comment gasił „Kanban”, choć kanban_task_assigned dalej czekał nieprzeczytany (i przez `storage`
               gasł we wszystkich kartach). TERAZ sekcja gaśnie tylko, gdy ŻADNA nieprzeczytana pozycja spoza odczytanych
               nie prowadzi do tej samej sekcji — niezależnie od kodu. */
            const kluczeSekcji = (kod) => (NOTIFICATION_SECTION_MAP[kod] || []).map(pathToPageKey);
            const doWygaszenia = [];
            kody.forEach((kod) => {
                kluczeSekcji(kod).forEach((klucz) => {
                    const zostalo = lista.some((n) => !n.isRead && !ids.has(String(n.id)) && n.typeCode
                        && kluczeSekcji(n.typeCode).includes(klucz));
                    if (!zostalo) doWygaszenia.push(klucz);
                });
            });
            if (doWygaszenia.length === 0) return;
            setLastVisited((prev) => {
                const teraz = new Date().toISOString();
                const updated = { ...prev };
                doWygaszenia.forEach((k) => { updated[k] = teraz; });
                persist(kluczeRef.current.visited, updated);
                return updated;
            });
        };
        window.addEventListener(ZDARZENIA_SYNC.read, naOdczyt);
        window.addEventListener(ZDARZENIA_SYNC.readMany, naOdczyt);
        return () => {
            window.removeEventListener(ZDARZENIA_SYNC.read, naOdczyt);
            window.removeEventListener(ZDARZENIA_SYNC.readMany, naOdczyt);
        };
    }, [persist]);

    // Oznacz sekcje jako mające nową treść (na podany czas). Jeśli użytkownik jest WŁAŚNIE
    // w tej sekcji — od razu „zobaczone" (nie pokazujemy kropki na otwartej stronie).
    const markContent = useCallback((paths, whenIso) => {
        if (!paths || paths.length === 0) return;
        const now = whenIso || new Date().toISOString();
        const currentSection = pathToSectionKey(sciezkaRef.current);
        const currentPage = pathToPageKey(sciezkaRef.current);

        setLatestContent(prev => {
            const next = { ...prev };
            paths.forEach(p => {
                const key = pathToPageKey(p); // ścieżka z mapy decyduje o poziomie: sekcja albo podstrona
                if (!next[key] || next[key] < now) next[key] = now;
            });
            persist(kluczeRef.current.content, next);
            return next;
        });

        // Treść dla miejsca, które właśnie oglądam → od razu „zobaczona" (na TYM poziomie).
        const trafione = paths.map(pathToPageKey).filter(k => k === currentSection || k === currentPage);
        if (trafione.length > 0) {
            setLastVisited(prev => {
                const teraz = new Date().toISOString();
                const updated = { ...prev };
                trafione.forEach(k => { updated[k] = teraz; });
                persist(kluczeRef.current.visited, updated);
                return updated;
            });
        }
    }, [persist]);

    /**
     * Called by NotificationContext when a new notification arrives.
     */
    const onNotification = useCallback((notification) => {
        if (!notification?.typeCode) return;
        const paths = NOTIFICATION_SECTION_MAP[notification.typeCode];
        if (!paths) return;
        markContent(paths, notification.createdAt);
    }, [markContent]);

    /**
     * Nasłuch zdarzeń real-time (SignalR → window CustomEvent): nowa/zmieniona treść → kropka.
     */
    useEffect(() => {
        const handlers = Object.entries(REALTIME_EVENT_SECTION_MAP).map(([evt, paths]) => {
            /* [poprawka NOWE-ZGLOSZENIE 2026-09-28] Zdarzenie z WŁASNEJ akcji (wniosek złożony z okna „Utwórz nowe
               zgłoszenie” odświeża listy wniosków tym samym sygnałem, którym SignalR ożywia strony) nie jest „nową
               aktywnością” — kropka zapala się od cudzej zmiany, nie od tego, co człowiek sam przed chwilą zrobił. */
            const handler = (e) => { if (e?.detail?.wlasnaAkcja) return; markContent(paths); };
            window.addEventListener(evt, handler);
            return [evt, handler];
        });
        return () => handlers.forEach(([evt, handler]) => window.removeEventListener(evt, handler));
    }, [markContent]);

    /**
     * [brief właściciela 2026-08-31, pkt 4.2a] KROPKA PRZY „OBSŁUDZE HR" TAKŻE PO ZALOGOWANIU.
     *
     * <p>Zdarzenie SignalR zapala kropkę tylko wtedy, gdy człowiek siedzi przy otwartej karcie.
     * Komentarz dopisany w nocy albo w czasie urlopu nie zostawiłby po sobie nic. Dlatego przy
     * starcie pytamy serwer o AGREGAT nieodczytanych („ile jest dla mnie nowego i kiedy przyszło
     * ostatnie") i zapalamy kropkę tą samą drogą, co zdarzenia na żywo — więc gaśnie tak samo,
     * czyli po wejściu do sekcji.</p>
     *
     * <p>Import jest LENIWY, a błąd zjadany: kropka to wygoda, nie dane. Brak sieci albo starszy
     * backend nie mogą wywrócić menu bocznego, przez które przechodzi cała nawigacja aplikacji.</p>
     */
    useEffect(() => {
        if (!accountId) {
            /* [WYDAJNOŚĆ/BEZPIECZEŃSTWO 2026-09-12] Wylogowanie (albo start przed odczytaniem sesji)
               ZDEJMUJE straż. Bez tego po przelogowaniu na to samo konto w tej samej karcie agregat
               nie pobrałby się już nigdy, a straż trzymałaby identyfikator poprzedniej sesji. */
            agregatKomentarzyPobrany = null;
            return undefined;
        }

        /* ⚠️ [2026-09-08] ZAPYTANIE RAZ NA KONTO, NIE RAZ NA NAWIGACJĘ I NIE RAZ NA INSTANCJĘ.

           BYŁO ŹLE: w zależnościach stoi `markContent`, którego tożsamość zmienia się przy KAŻDEJ
           zmianie `location.pathname` (jest w jego własnych zależnościach). Efekt odpalał się więc
           przy każdym przejściu między stronami — a `GET /api/hr-requests/comment-activity` liczy
           agregat po wszystkich wnioskach użytkownika. To jest zapytanie startowe („co przyszło,
           kiedy mnie nie było"), a nie zapytanie nawigacyjne: jego wynik nie zależy od tego, na
           którą stronę właśnie wchodzisz.

           Drugi powód, dla którego to naprawiam TERAZ: od dziś hak ma więcej niż jednego
           konsumenta (menu boczne + pasek zakładek „Mojego Działu"), a każda instancja odpalała
           własną kopię tego efektu. Bez tej straży dołożenie kropek do paska podwoiłoby ruch.

           Straż jest MODUŁOWA, nie instancyjna — inaczej nie widziałaby drugiej instancji.
           Trzyma id konta, więc przelogowanie na inne konto pobiera agregat na nowo. */
        if (agregatKomentarzyPobrany === accountId) return undefined;
        agregatKomentarzyPobrany = accountId;

        let aktualne = true;
        (async () => {
            try {
                const { default: hrDataService } = await import("../services/hrDataService");
                const agregat = await hrDataService.getCommentActivity();
                if (!aktualne || !agregat || agregat.totalUnread <= 0) return;
                markContent(REALTIME_EVENT_SECTION_MAP["hr-comment-added"], agregat.lastCommentAt);
            } catch {
                /* Kropka jest wygodą — cisza jest tu właściwą reakcją. Straż ZDEJMUJEMY, żeby
                   chwilowy brak sieci nie zablokował pobrania agregatu do końca sesji. */
                if (agregatKomentarzyPobrany === accountId) agregatKomentarzyPobrany = null;
            }
        })();
        return () => { aktualne = false; };
    }, [accountId, markContent]);

    /**
     * Mark a section as visited (clears the dot).
     */
    const markVisited = useCallback((path) => {
        const key = pathToSectionKey(path);
        if (!key || key === "/") return;
        const pageKey = pathToPageKey(path);
        setLastVisited(prev => {
            const teraz = new Date().toISOString();
            const updated = { ...prev, [key]: teraz };
            if (pageKey !== key) updated[pageKey] = teraz; // wizyta na podstronie gasi też jej własną kropkę
            persist(kluczeRef.current.visited, updated);
            return updated;
        });
    }, [persist]);

    /**
     * Auto-mark current page as visited on route change.
     */
    /* `STORAGE_KEY` stoi w zależnościach ŚWIADOMIE, mimo że `markVisited` jest już stabilne:
       hook startuje na kubełku „anon" (konto nieznane) i przełącza się na kubełek konta dopiero
       po odczytaniu sesji. Bez tej zależności wizyta trwająca w chwili przełączenia nie zostałaby
       zapisana pod kontem użytkownika — i kropka przy właśnie oglądanej sekcji wróciłaby po
       przejściu na inną stronę. (Przed 2026-09-12 dawała to zmiana tożsamości `markVisited`.) */
    useEffect(() => {
        const key = pathToSectionKey(location.pathname);
        if (key && key !== "/") {
            markVisited(location.pathname);
        }
    }, [location.pathname, markVisited, STORAGE_KEY]);

    /**
     * Check if a sidebar path has new content.
     */
    const hasNew = useCallback((path) => {
        const key = pathToSectionKey(path);
        if (!key || key === "/") return false;
        const noweNaPoziomie = (k, ogladaneTeraz) => {
            if (ogladaneTeraz) return false; // nie pokazuj kropki na tym, co właśnie oglądasz
            const latest = latestContent[k];
            if (!latest) return false;
            const visited = lastVisited[k];
            if (!visited) return true; // never visited → new!
            return latest > visited;
        };
        // Poziom sekcji — jak dotąd: treść „dla całej sekcji" świeci przy każdym wejściu do niej.
        const zSekcji = noweNaPoziomie(key, pathToSectionKey(location.pathname) === key);
        const pageKey = pathToPageKey(path);
        if (pageKey === key) {
            // Pytanie o CAŁĄ sekcję (np. rodzic „Mój Dział" albo wpis /moj-dzial): świeci, gdy świeci sekcja
            // ALBO którakolwiek jej podstrona — rodzic agreguje dzieci, więc „coś czeka we wnioskach działu"
            // nadal zapala „Mój Dział", tylko że teraz wiadomo też, KTÓRĄ podzakładkę.
            const zPodstron = Object.keys(latestContent).some(k =>
                k.startsWith(key + "/") && noweNaPoziomie(k, pathToPageKey(location.pathname) === k));
            return zSekcji || zPodstron;
        }
        // Pytanie o KONKRETNĄ podstronę (podpozycja menu): sekcja jak dotąd + jej własny klucz.
        const zPodstrony = jestKluczemPodstrony(pageKey)
            && noweNaPoziomie(pageKey, pathToPageKey(location.pathname) === pageKey);
        return zSekcji || zPodstrony;
    }, [latestContent, lastVisited, location.pathname]);

    return { hasNew, markVisited, onNotification };
}
